import React, { useState, useRef, useEffect } from 'react';
import { Event } from '../types/Event';
import { eventService } from '../services/eventService.ts';
import { imageService } from '../services/imageService.ts';
import { GeoPoint, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config.ts';
import './EventForm.css';
import MapPreview from './MapPreview.tsx';
import { classifyEventType } from '../services/aiService.ts';
import { EVENT_CATEGORIES, EVENT_TYPES, getTypesByCategory, OLD_TYPE_TO_CATEGORY_MAP, EventCategory } from '../constants/eventTypes.ts';
import { classifyEventTypeDetailed } from '../services/aiService';
import { functions } from '../firebase.ts';
import { httpsCallable } from 'firebase/functions';

export interface MissingFields {
  [key: string]: boolean;
}

interface EventFormProps {
  event?: Event | null;
  onSubmit: () => void;
  missingFields?: MissingFields;
}

interface FormData extends Omit<Event, 'startDateTime' | 'endDateTime' | 'category'> {
  type?: string;
  category: EventCategory;
  halls?: string[];
  is_premieres?: boolean[];
  english_name?: string;
  genre?: string;
  name?: string;
  date_scraped?: string;
  is_recurring?: boolean;
  startDateTime: Timestamp[];
  endDateTime: Timestamp[];
  duration?: number;
  lat_long: GeoPoint;
}

const convertToTimestamp = (value: string | Timestamp | Date): Timestamp => {
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) return Timestamp.fromDate(value);
  return Timestamp.fromDate(new Date(value));
};

const convertToGeoPoint = (value: { lat: number; lng: number } | GeoPoint | null): GeoPoint | null => {
  if (!value) return null;
  if (value instanceof GeoPoint) return value;
  return new GeoPoint(value.lat, value.lng);
};

export const EventForm: React.FC<EventFormProps> = ({ event, onSubmit, missingFields }) => {
  const defaultCategory: EventCategory = 'Art & Exhibitions';
  const [formData, setFormData] = useState<FormData>(() => ({
    title: event?.title || '',
    description: event?.description || '',
    place: event?.place || '',
    event_type: event?.event_type || '',
    type: event?.type || '',
    category: (event?.category as EventCategory) || defaultCategory,
    isPublic: event?.isPublic ?? true,
    lat_long: event?.lat_long || new GeoPoint(32, 34),
    date: event?.date || new Date(),
    startDateTime: event?.startDateTime ? 
      (Array.isArray(event.startDateTime) 
        ? event.startDateTime.map(dt => convertToTimestamp(dt))
        : [convertToTimestamp(event.startDateTime)])
      : [Timestamp.now()],
    endDateTime: event?.endDateTime ?
      (Array.isArray(event.endDateTime)
        ? event.endDateTime.map(dt => convertToTimestamp(dt))
        : [convertToTimestamp(event.endDateTime)])
      : [Timestamp.now()],
    userId: event?.userId || 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
    user_name: event?.user_name || 'admin',
    likes: event?.likes || 0,
    comments: event?.comments || 0,
    shares: event?.shares || 0,
    Image_gallery_url: event?.Image_gallery_url || [],
    imageUrl: event?.imageUrl || '',
    poster_url: event?.poster_url || '',
    event_url: event?.event_url || '',
    _localImageFiles: event?._localImageFiles || [],
    duration: event?.duration,
    halls: event?.halls || [],
    is_premieres: event?.is_premieres || [],
    english_name: event?.english_name,
    genre: event?.genre,
    date_scraped: event?.date_scraped,
    is_recurring: event?.is_recurring
  }));

  const [images, setImages] = useState<File[]>([]);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [existingImages, setExistingImages] = useState<string[]>(
    event?.Image_gallery_url || []
  );
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [currentMissingFields, setCurrentMissingFields] = useState<MissingFields | undefined>(missingFields);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isClassifying, setIsClassifying] = useState(false);

  useEffect(() => {
    console.log('API Key:', process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
    if (window.google && autocompleteInputRef.current) {
      const autocomplete = new google.maps.places.Autocomplete(autocompleteInputRef.current, {
        types: ['establishment']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace() as google.maps.places.PlaceResult & {
          geometry: { location: google.maps.LatLng }
        };
        
        if (place.geometry?.location) {
          setFormData(prev => ({
            ...prev,
            place: place.name || '',
            lat_long: new GeoPoint(
              place.geometry.location.lat(),
              place.geometry.location.lng()
            )
          }));
        }
      });
    }
  }, []);

  useEffect(() => {
    if (event) {
      // Update form data
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || '',
        place: event.place || '',
        isPublic: event.isPublic || false,
        category: (event?.category as EventCategory) || defaultCategory,
        lat_long: event.lat_long 
          ? new GeoPoint(
              event.lat_long.latitude,
              event.lat_long.longitude
            )
          : new GeoPoint(32, 34.8),
        startDateTime: event.startDateTime 
          ? (Array.isArray(event.startDateTime) 
            ? event.startDateTime.map(dt => convertToTimestamp(dt))
            : [convertToTimestamp(event.startDateTime)])
          : [Timestamp.now()],
        endDateTime: event.endDateTime 
          ? (Array.isArray(event.endDateTime)
            ? event.endDateTime.map(dt => convertToTimestamp(dt))
            : [convertToTimestamp(event.endDateTime)])
          : [Timestamp.now()],
        userId: event.userId || 'K09eqzpzpOYAzcdl8k2vtujaJLO2',
        user_name: event.user_name || 'niv.beeri@gmail.com',
        likes: event.likes || 0,
        comments: event.comments || 0,
        shares: event.shares || 0,
        Image_gallery_url: event.Image_gallery_url || [],
        name: event.name || '',
        english_name: event.english_name || '',
        genre: event.genre || '',
        hostID: event.hostID || '',
        date_scraped: event.date_scraped || '',
        is_recurring: event.is_recurring || false,
        halls: event.halls || [],
        is_premieres: event.is_premieres || [],
        event_url: event.event_url || '',
        poster_url: event.poster_url || '',
        special_event: event.special_event || '',
        duration: event.duration || 0
      });
    }
  }, [event]);

  useEffect(() => {
    if (event?.lat_long) {
      console.log('📍 Updating coordinates:', event.lat_long);
      setFormData(prev => ({
        ...prev,
        lat_long: new GeoPoint(
          event.lat_long.latitude,
          event.lat_long.longitude
        )
      }));
    }
  }, [event]);

  useEffect(() => {
    setCurrentMissingFields(missingFields);
  }, [missingFields]);

  useEffect(() => {
    if (event?.event_type && !selectedCategory) {
      const category = OLD_TYPE_TO_CATEGORY_MAP[event.event_type] || EVENT_CATEGORIES[0];
      setSelectedCategory(category);
    }
  }, [event?.event_type]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCompressing(true);
    const files = Array.from(e.target.files || []);
    const compressedFiles: File[] = [];

    try {
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const compressed = await imageService.compressImage(file);
          compressedFiles.push(compressed);
        }
      }
      setImages(compressedFiles);
    } catch (error) {
      console.error('Error handling images:', error);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (event?.id) {
        // For updates, merge existing and new images
        const updatedUrls = [...existingImages];
        if (images.length > 0) {
          const newUrls = await imageService.processImages(images, event.id);
          updatedUrls.push(...newUrls);
        }
        await eventService.updateEvent(event.id, {
          ...formData,
          Image_gallery_url: updatedUrls
        });
      } else {
        // For new events, only include non-empty and essential fields
        const essentialFields = {
          title: formData.title,
          description: formData.description,
          event_type: formData.event_type,
          type: formData.type,
          category: formData.category,
          place: formData.place,
          isPublic: formData.isPublic,
          lat_long: formData.lat_long,
          startDateTime: formData.startDateTime,
          userId: formData.userId,
          user_name: formData.user_name
        };

        // Add optional fields only if they have values
        const optionalFields = {
          ...(formData.endDateTime && { endDateTime: formData.endDateTime }),
          ...(formData.english_name && { english_name: formData.english_name }),
          ...(formData.genre && { genre: formData.genre }),
          ...(formData.hostID && { hostID: formData.hostID }),
          ...(formData.is_recurring && { is_recurring: formData.is_recurring }),
          ...(formData.halls && formData.halls.length > 0 && { halls: formData.halls }),
          ...(formData.is_premieres && formData.is_premieres.length > 0 && { is_premieres: formData.is_premieres }),
          ...(formData.event_url && { event_url: formData.event_url }),
          ...(formData.poster_url && { poster_url: formData.poster_url }),
          ...(formData.special_event && { special_event: formData.special_event }),
          ...(formData.duration !== undefined && { duration: formData.duration })
        };

        // Create event with only essential and non-empty fields
        await eventService.createEvent(
          { ...essentialFields, ...optionalFields } as Omit<Event, 'id'>,
          images
        );
      }
      onSubmit();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleImageDelete = async (urlToDelete: string) => {
    try {
      const updatedUrls = existingImages.filter(url => url !== urlToDelete);
      setExistingImages(updatedUrls);
      
      if (event?.id) {
        await eventService.updateEvent(event.id, {
          Image_gallery_url: updatedUrls
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const missingFieldStyle = {
    border: '2px solid #ff4444',
    backgroundColor: '#fff0f0'
  };

  const ImagePreview = ({ url }: { url: string }) => {
    const [imgSrc, setImgSrc] = useState<string>(url);
    const [error, setError] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await fetch(url, {
            mode: 'no-cors',
            headers: {
              'Origin': 'https://gis.tel-aviv.gov.il'
            }
          });
          
          // Create object URL from blob
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setImgSrc(objectUrl);
          setError(false);
        } catch (err) {
          console.error('Error loading image:', err);
          setError(true);
        }
      };

      loadImage();

      // Cleanup
      return () => {
        if (imgSrc.startsWith('blob:')) {
          URL.revokeObjectURL(imgSrc);
        }
      };
    }, [url]);

    if (error) {
      return <div>Error loading image</div>;
    }

    return <img src={imgSrc} alt="Event" style={{ maxWidth: '200px', height: 'auto' }} />;
  };

  const handleAIClassify = async () => {
    if (!formData.title) {
      alert('Please enter a title first');
      return;
    }

    setIsClassifying(true);
    try {
      // Using the proxy configured in package.json
      // Instead of the full URL, we just use the path
      const response = await fetch('/classifyEventTypeDetailed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description || ''
        })
      });

      if (!response.ok) {
        console.error('Classification API error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText || 'Unknown error' };
        }
        
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Classification result:', result);
      const { type, category, confidence } = result;

      // Always update the form with AI suggestion
      setSelectedCategory(category);
      setFormData(prev => ({
        ...prev,
        type,
        category
      }));

      // Show warning for low confidence
      if (confidence < 0.3) {
        alert(`Low confidence classification (${Math.round(confidence * 100)}%). Please verify the suggested type and category.`);
      } else if (confidence < 0.5) {
        console.log('Moderate confidence classification:', result);
      }
    } catch (error) {
      console.error('Error classifying event:', error);
      
      // Fallback to manual classification
      alert('Failed to classify event automatically. Please select type and category manually.');
      
      // Optionally implement a fallback classification based on keywords in the title
      const fallbackClassification = inferEventCategory(formData.title);
      if (fallbackClassification) {
        console.log('Using fallback classification:', fallbackClassification);
        setSelectedCategory(fallbackClassification.category);
        setFormData(prev => ({
          ...prev,
          type: fallbackClassification.type,
          category: fallbackClassification.category
        }));
      }
    } finally {
      setIsClassifying(false);
    }
  };

  // Fallback classification function
  const inferEventCategory = (title: string): { category: EventCategory; type: string } | null => {
    if (!title) return null;
    
    const lowerTitle = title.toLowerCase();
    
    // Check for music/concert keywords
    if (/concert|music|band|dj|live show|performance|gig|jam|stage|show/i.test(lowerTitle)) {
      return { 
        category: 'Live Show & Music', 
        type: /comedy|stand.?up/i.test(lowerTitle) ? 'Stand-up Comedy' : 'Concerts & Gigs' 
      };
    }
    
    // Check for party keywords
    if (/party|club|dance|nightlife|celebration|festival/i.test(lowerTitle)) {
      return { category: 'Party', type: 'House Parties' };
    }
    
    // Check for art keywords
    if (/art|exhibition|gallery|museum|painting|sculpture/i.test(lowerTitle)) {
      return { category: 'Art & Exhibitions', type: 'Exhibitions & Galleries' };
    }
    
    // Check for food keywords
    if (/food|drink|wine|beer|tasting|cooking|culinary|restaurant/i.test(lowerTitle)) {
      return { category: 'Food & Drink', type: 'Wine, Beer & More' };
    }
    
    // Default fallback
    return { category: 'Social Meetups', type: 'Casual Hangouts' };
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-header">
        <h2>{event ? 'Edit Event' : 'Create New Event'}</h2>
        <p>Fill in the details below to {event ? 'update your' : 'create a new'} event</p>
      </div>

      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            style={currentMissingFields?.title ? missingFieldStyle : undefined}
            required
          />
          {currentMissingFields?.title && (
            <small className="error-text">This field was missing in the CSV</small>
          )}
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            style={currentMissingFields?.description ? missingFieldStyle : undefined}
            required
          />
          {currentMissingFields?.description && (
            <small className="error-text">This field was missing in the CSV</small>
          )}
        </div>

        <div className="form-group">
          <label>Event Category & Type</label>
          <div className="type-selection">
            <select
              value={selectedCategory}
              onChange={(e) => {
                const category = e.target.value as EventCategory;
                setSelectedCategory(category);
                setFormData(prev => ({
                  ...prev,
                  category: category,
                  type: getTypesByCategory(category)[0] || ''
                }));
              }}
              className={missingFields?.event_type ? 'error' : ''}
            >
              <option value="">Select Category</option>
              {EVENT_CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            <select
              value={formData.type || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                type: e.target.value
              }))}
              disabled={!selectedCategory}
              className={missingFields?.event_type ? 'error' : ''}
            >
              <option value="">Select Type</option>
              {selectedCategory && getTypesByCategory(selectedCategory as EventCategory).map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <button 
              type="button" 
              onClick={handleAIClassify}
              disabled={isClassifying || !formData.title}
              className="ai-classify-button"
            >
              {isClassifying ? '🤖 Thinking...' : '🤖 AI Suggest'}
            </button>
          </div>
          {missingFields?.event_type && (
            <span className="error-message">Please select an event type</span>
          )}
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="isPublic"
            checked={formData.isPublic}
            onChange={e => setFormData({ ...formData, isPublic: e.target.checked })}
          />
          <label htmlFor="isPublic">Make this event public</label>
        </div>

        <div className="form-group">
          <label>Event Images</label>
          {existingImages.length > 0 && (
            <div className="existing-images">
              <h4>Current Images</h4>
              <div className="image-preview-grid">
                {existingImages.map((url, index) => (
                  <div key={index} className="image-preview-item">
                    <img 
                      src={url} 
                      alt={`Event ${index + 1}`}
                      onClick={() => setExpandedImage(url)}
                    />
                    <button 
                      type="button" 
                      className="delete-image-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageDelete(url);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="file-input-group">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              disabled={isCompressing}
            />
            <p>
              {isCompressing 
                ? 'Compressing images...' 
                : 'Drag and drop images or click to select (will be compressed to max 80KB)'}
            </p>
          </div>
        </div>

        {expandedImage && (
          <div className="image-modal" onClick={() => setExpandedImage(null)}>
            <img 
              src={expandedImage} 
              alt="Expanded view" 
              className="modal-image"
            />
          </div>
        )}

        <div className="form-section">
          <h3>Event Times</h3>
          <div className="form-group">
            <label>Is Recurring Event</label>
            <input
              type="checkbox"
              checked={formData.is_recurring}
              onChange={e => setFormData({ ...formData, is_recurring: e.target.checked })}
            />
          </div>

          {Array.isArray(formData.startDateTime) && formData.startDateTime.map((_, index) => (
            <div key={index} className="datetime-group">
              <h4>Time Slot {index + 1}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="datetime-local"
                    value={formData.startDateTime[index] instanceof Timestamp 
                      ? new Date(formData.startDateTime[index].seconds * 1000).toISOString().slice(0, 16)
                      : new Date().toISOString().slice(0, 16)}
                    onChange={e => {
                      const newStartTimes = [...formData.startDateTime];
                      newStartTimes[index] = Timestamp.fromDate(new Date(e.target.value));
                      setFormData({ ...formData, startDateTime: newStartTimes });
                    }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="datetime-local"
                    value={formData.endDateTime[index] instanceof Timestamp 
                      ? new Date(formData.endDateTime[index].seconds * 1000).toISOString().slice(0, 16)
                      : ''}
                    onChange={e => {
                      const newEndTimes = [...formData.endDateTime];
                      newEndTimes[index] = e.target.value 
                        ? Timestamp.fromDate(new Date(e.target.value))
                        : Timestamp.fromDate(new Date());
                      setFormData({ ...formData, endDateTime: newEndTimes });
                    }}
                  />
                </div>

                {index > 0 && (
                  <button
                    type="button"
                    className="remove-time-btn"
                    onClick={() => {
                      const newStartTimes = formData.startDateTime.filter((_, i) => i !== index);
                      const newEndTimes = formData.endDateTime.filter((_, i) => i !== index);
                      setFormData({
                        ...formData,
                        startDateTime: newStartTimes,
                        endDateTime: newEndTimes
                      });
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}

          {formData.is_recurring && (
            <button
              type="button"
              className="add-time-btn"
              onClick={() => {
                setFormData({
                  ...formData,
                  startDateTime: [...formData.startDateTime, Timestamp.fromDate(new Date())],
                  endDateTime: [...formData.endDateTime, Timestamp.fromDate(new Date())]
                });
              }}
            >
              Add Another Time Slot
            </button>
          )}
        </div>

        <div className="form-section">
          <h3>Location</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Latitude:</label>
              <input
                type="number"
                step="any"
                value={formData.lat_long?.latitude}
                onChange={e => setFormData({
                  ...formData,
                  lat_long: new GeoPoint(
                    parseFloat(e.target.value),
                    formData.lat_long?.longitude || 0
                  )
                })}
                className={currentMissingFields?.lat_long ? 'field-error' : ''}
              />
            </div>
            <div className="form-group">
              <label>Longitude:</label>
              <input
                type="number"
                step="any"
                value={formData.lat_long?.longitude}
                onChange={e => setFormData({
                  ...formData,
                  lat_long: new GeoPoint(
                    formData.lat_long?.latitude || 0,
                    parseFloat(e.target.value)
                  )
                })}
                className={currentMissingFields?.lat_long ? 'field-error' : ''}
              />
            </div>
          </div>
          <div className="map-preview">
            <MapPreview position={formData.lat_long || new GeoPoint(0, 0)} />
          </div>
        </div>

        <div className="form-group">
          <label>Place Name</label>
          <input
            type="text"
            value={formData.place}
            onChange={e => setFormData({ ...formData, place: e.target.value })}
            style={currentMissingFields?.place || formData.place === 'Tel-Aviv' ? missingFieldStyle : undefined}
            placeholder="Venue name"
            required
          />
          {(currentMissingFields?.place || formData.place === 'Tel-Aviv') && (
            <small className="error-text">Please enter a specific venue</small>
          )}
        </div>

        <div className="form-group">
          <label>Host ID</label>
          <input
            type="text"
            value={formData.hostID}
            onChange={e => setFormData({ ...formData, hostID: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Duration (minutes)</label>
          <input
            type="number"
            value={formData.duration}
            onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })}
          />
        </div>

        <div className="form-group">
          <label>Hall</label>
          <input
            type="text"
            value={formData.halls?.join(', ') || ''}
            onChange={e => setFormData({ ...formData, halls: e.target.value.split(',').map(s => s.trim()) })}
          />
        </div>
      </div>

      {formData.special_event === "movie" && (
        <>
          <div className="form-section">
            <h3>Movie Details</h3>
            <div className="form-group">
              <label>Hebrew Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>English Name</label>
              <input
                type="text"
                value={formData.english_name}
                onChange={e => setFormData({ ...formData, english_name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Genre</label>
              <input
                type="text"
                value={formData.genre}
                onChange={e => setFormData({ ...formData, genre: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration}
                onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>Hall</label>
              <input
                type="text"
                value={formData.halls?.join(', ') || ''}
                onChange={e => setFormData({ ...formData, halls: e.target.value.split(',').map(s => s.trim()) })}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Movie Media</h3>
            <div className="form-group">
              <label>Event URL</label>
              <input
                type="url"
                value={formData.event_url}
                onChange={e => setFormData({ ...formData, event_url: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Poster URL</label>
              <input
                type="url"
                value={formData.poster_url}
                onChange={e => setFormData({ ...formData, poster_url: e.target.value })}
              />
            </div>
          </div>
        </>
      )}

      <div className="form-section">
        <h3>Images</h3>
        <div className="current-images">
          {formData.Image_gallery_url?.map((url, index) => (
            <div key={index} className="image-preview-item">
              <img 
                src={url} 
                alt={`Event ${index + 1}`} 
                style={{ maxWidth: '200px', height: 'auto' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'placeholder-image-url.jpg'; // Add a placeholder image URL
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="button-group">
        <button type="button" className="button-secondary" onClick={onSubmit}>
          Cancel
        </button>
        <button type="submit" className="button-primary">
          {event ? 'Update Event' : 'Create Event'}
        </button>
      </div>

      {event?.imageUrl && (
        <div className="form-group">
          <label>Event Image</label>
          <div className="image-preview">
            <img 
              src={event.imageUrl} 
              alt="Event preview" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '300px', 
                objectFit: 'contain' 
              }} 
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="event_url">Event URL:</label>
        <input
          type="url"
          id="event_url"
          value={formData.event_url || ''}
          onChange={e => setFormData({ ...formData, event_url: e.target.value })}
          className={missingFields?.event_url ? 'error' : ''}
          placeholder="https://..."
        />
      </div>
    </form>
  );
};

export default EventForm; 